import type { visioweb } from "types/visioweb";
import { PoiTool } from "./poi-tool";
import { type Readable, get, readable } from "svelte/store";
import type { EventType, ResourceLocale } from "types/visioweb/mapviewer";
import { 
  ConnectMapApp,
  type ConnectMapAppConfig,
  type ConnectMapEventDispatch,
  type Floor,
  ImplLayer } from "../app";
import { merge } from "lodash-es";
import type { POI } from "../poi";
import type { MapL10n } from "../translate";
import type { NavigationTranslations } from "types/visioweb/navigation";
import { defineCustomNavigationTranslator, initCustomNavigationTranslator } from './translate';

// import { VisioWebEssential } from '@public/lib/visioweb-1.12.3-20230530/visioweb.essential'


export type VisioMapAppConfig = ConnectMapAppConfig & {
  image_path: string;
  visio: Partial<visioweb.VisioWebEssentialParameters>;
  extra?: Partial<{
    font_family: string;
    initial_floor: string;
    zoom_scale_factor: number;
  }>;
} & (
  | { base_url?: string; map_hash: string; map_url?: never; }
  | { base_url?:  never; map_hash?: never; map_url: string; }
);

const DEFAULTS: Omit<Required<VisioMapAppConfig>, 
  'base_url' | 'initial_poi' | 'init_in_fullscreen'> = {
  locale: '',
  image_path: '',
  map_hash: '',
  map_url: '',
  visio: {},
  extra: {
    font_family: 'ui-system',
    initial_floor: '',
    zoom_scale_factor: 1.2,
  },
}

type VisioL10n = MapL10n & {
  visio: NavigationTranslations
}

export function splitCode(spaceCode = '') {
  return spaceCode.split(/\s*,\s*/);
}

export class VisioMapLayer extends ImplLayer {

  readonly visio_data: VisioData;
  readonly config: Required<VisioMapAppConfig>;
  readonly element: HTMLElement;
  readonly essential: VisioWebEssential;

  constructor (app: VisioMapApp, config: Required<VisioMapAppConfig>, element: HTMLElement) {

    super(app);

    defineCustomNavigationTranslator();
    
    this.config = config;
    this.element = element;
    this.essential = new VisioWebEssential({ element, imagePath: config.image_path });
    this.visio_data = new VisioData(this);

    this.essential.setParameters({ parameters: config.visio });

    // @ts-ignore: TODO remove later
    window['__essential__'] = this.essential;
  }

  /**
   * Should be called only after the {element} has reach new dimensions.
   */
  resize () {
    if (this.element) {
      this.essential?._mapviewer.resize(this.element.offsetWidth, this.element.offsetHeight);
    }
  }

  async selectPOI(poi: POI) {
    
    let first_id;
    try {
      this.essential.content.resetHighlightedPlace();
      [first_id] = this.getRoutablePOIs(poi.id);
      const place = this.essential.content.getPlace({ id: first_id });
      //const viewpoint = this.essential.venue.getViewpoint();
      
      // 1st goto correct floor
      const need_floor_change = this.essential.venue.currentFloorID != place.floor;
      const floor = need_floor_change && this.app.findFloor(place.floor);
      
      if (floor) {
        await this.app.setCurrentFloor(floor);
      }
      
      // 2nd pan & zoom to target ()
      await this.essential.venue.goToPlace({
        id: first_id,
        radius: 200
      });
      
      // 3rd highlight target (do not wait as this uses default headeing)
      this.essential.content.setActivePlace({ place: first_id });

      poi.routable = true;

    } catch (error) {
      console.error(`Can't route to '${poi.id}'`, poi, error);
      poi.routable = false;
    }
  }

  getVgPOI(id: string) {
    const [poi] = this.essential._mapviewer.getPOI(id) || [];
    return poi;
  }

  getRoutablePOIs(spaceCode: string) {
    return splitCode(spaceCode).filter((id) => this.essential._mapviewer.getRoutingNode(id));
  }

}

export class VisioMapApp extends ConnectMapApp<VisioMapLayer> {

  protected _config: VisioMapAppConfig;

  private poi_tool!: PoiTool;

  constructor (config: VisioMapAppConfig) {
    super(config);
    this._config = config;
    this.poi_tool = new PoiTool(this);

    this.unsubscribers.push(...[
      this.current_floor.subscribe(floor => floor && this.poi_tool.setLabelsForFloor(floor)),
    ]);
  }

  async _initializeImplLayer (element: HTMLElement, dispatch: ConnectMapEventDispatch) {
    await VisioMapApp.isVisiowebAvailable();


    const base_configs: Partial<VisioMapAppConfig> = {
      extra: {
        font_family: ImplLayer.getFontFamily(element),
      },
      visio: {
        locale: {
          language: this.lang,
        }
      }
    }

    if ('map_url' in this._config) {
      const [, hash] = this._config.map_url?.match(/https?:\/\/mapserver.visioglobe.com\/([^\/]*)/) || [];
      this._config.visio.hash = hash
    }
    else if ('base_url' in this._config) {
      this._config.visio.baseURL = this._config.base_url;
      this._config.visio.hash = this._config.map_hash;
    }

    const config: Required<VisioMapAppConfig> = merge({}, DEFAULTS, base_configs, this.config)
    this._config = config;

    const layer = new VisioMapLayer(this, config, element);

    layer.essential.onObjectMouseUp = ({ targetElement }) => {
      let options;
      let id: string | undefined;
         
      if ('options' in targetElement) { 
        // Label or icon
        options = targetElement.options();
        id = options.id
        
      } else if ('id' in targetElement.vg) {
        // Surface
        id = targetElement.vg.id;
      }
      
      if (id) {
        layer.essential.content.setActivePlace({ place: id });
        const poi = this.poi_data.getPoiByID(id);
        console.log('poi', id, poi);
        poi &&  dispatch('tap_poi', poi);
      }
      
    }


    try {
      await layer.essential.createMapviewer();
      initCustomNavigationTranslator(layer.essential, this.lang);
    } catch (error) {
      console.warn('Could not load map!');
      console.error(error);
    }

    // Catch Sentry SITE-22-A
    try {
      // Navigation translations
      const l10n = this.l10n as VisioL10n;
      const switch_lang = layer.essential.navigation.translator.addOrUpdateLanguage(
        this.lang, JSON.stringify(l10n.visio)
      );
      if (switch_lang) {
        layer.essential.navigation.languageCode = this.lang;
      }
    } catch (error) {
      console.error(error);
    }

    // const nt = layer.essential._mapviewer.navigationTranslator;
    // layer.essential._mapviewer.navigationTranslator = {
    //   addOrUpdateLanguage: (lang, json_string) =>  nt.addOrUpdateLanguage(lang, json_string),
    //   getLanguage: (lang) => nt.getLanguage(lang),
    //   getLanguageCodes: () => nt.getLanguageCodes(),
    //   setFloors: (floors) => nt.setFloors(floors),
    //   translateInstructions(steps, lang) {
    //     console.log('translateInstructions', steps, lang);
    //     return nt.translateInstructions(steps, lang);
    //   },
    //   updateDurationForLanguage: (key, lang, localized) => nt.updateDurationForLanguage(key, lang, localized),
    //   updateKeywordForLanguage: (key, lang, localized) => nt.updateKeywordForLanguage(key, lang, localized),
    //   updateManeuverForLanguage: (key, lang, localized) => nt.updateManeuverForLanguage(key, lang, localized),
    //   updateNextManeuverForLanguage: (key, lang, localized) => nt.updateNextManeuverForLanguage(key, lang, localized),
    // }
    
    return layer;
  }

  async _onceImplLayerReady () {
    await this.setBuilding();
  }

  async initialize(element: HTMLElement, dispatch: ConnectMapEventDispatch): Promise<void> {
    await super.initialize(element, dispatch);
  }

  _destroy() {
    return this.impl_layer?.essential.destroyMapviewer();
  }

  getExtraResource <K extends keyof ResourceLocale, T extends ResourceLocale[K]>(locale_key: K, lang?: string): T | null {
    lang = lang || this.lang || 'default';
    const extra = this.impl_layer.essential._mapviewer.getExtraData();
    if (!extra) return null;
    if (!extra.resources[lang]) {
      lang = 'default';
    }
    const locale = extra.resources[lang].localized.locale[lang] as ResourceLocale;
    return locale[locale_key] as T;
  }

  async setBuilding (id?: string, floor_id?: string) {
    const { essential } = this.impl_layer;
    const { venue } = essential;
    const { layout, currentBuildingID, currentFloorID } = venue;
    if (!id && floor_id) {
      [id] = floor_id.split('-');
    }

    const venue_layout = this.getExtraResource('venueLayout') || {};
    const building = layout.buildingByID[id || currentBuildingID];

    const floors = building.floors.map(floor => ({
      id: floor.id,
      index: floor.levelIndex,
      name: venue_layout[floor.id]?.name,
      short_name: venue_layout[floor.id]?.shortName,
    }))
    .sort((a,b) => b.index - a.index) // descending order

    const current_floor = floors.find(f => f.id == currentFloorID);
    
    this.floors.set(floors);
    await (current_floor && this.setCurrentFloor(current_floor));
  }

  async setCurrentFloor(floor: Floor): Promise<void> {
    try {
      super.setCurrentFloor(floor);
      await this.impl_layer.essential.venue.goToFloor(floor);
    } catch (error) {
      console.error(error);
    }
  }

  async computeRoute() {
    // Set stores labels for all floors:
    this.poi_tool = new PoiTool(this);
    const floors = get(this.floors);
    this.poi_tool.checkLabelsForAllFloors(floors);
    
    const from = get(this.src_poi);
    const to = get(this.dst_poi);
    if (!from || !to) {
      throw new Error('Route setup not complete')
    }
    const route_nodes = { from: from.id, to: to.id };
    const { essential } = this.impl_layer;
    
    await essential.venue.goToPlace(from)
    await essential.route.setFrom(route_nodes);
    await essential.route.setTo(route_nodes);
  }

  clearRouting() {
    this.impl_layer.essential.route.clear();
  }

  setRouteOption (option: 'shortest' | 'accessible') {
    this.impl_layer.essential.route.accessible = option == 'accessible';
  }
  
  static async isVisiowebAvailable(poll_limit = 100) {
    const test = () => (poll_limit--, 'visioweb' in window && 'VisioWebEssential' in window);
    while (!test()) {
      if (poll_limit < 0) throw new Error('Dependencies "visioweb" and "VisioWebEssential" not available!');
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return true;
  }

}


// class VisioSearchIndex extends SearchIndex {

//   updateServiceIndexes () {
//     console.log('Implement service indexing');
//   }
// }

class VisioData {

  readonly map_initialized: Readable<boolean>;
  readonly load_complete : Readable<boolean>;

  constructor (layer: VisioMapLayer) {

    const once = <T>(type: EventType, init: T, value: T) => readable(init, 
      set => layer.essential.visiowebAPIs.once(type, (e) => (console.log(type, e), set(value)))
    );
    
    this.map_initialized = once('initializeCompleted', false, true);
    this.load_complete = once('loadCompleted', false, true);
  }
}